import { createSimpleFormatter, ProductType } from "@grandvisionhq/frk-routing";
import getConfig from "next/config";
const createAuthFormatters = () => {
  return {
    "auth.create": createSimpleFormatter("my-account/create"),
    "auth.verify": createSimpleFormatter("my-account/verify"),
    "auth.link-expired": createSimpleFormatter("my-account/link-expired"),
    "auth.login": createSimpleFormatter("my-account/login"),
    "auth.signup": createSimpleFormatter("/my-account/signup"),
    "auth.logout": createSimpleFormatter("my-account/logout"),
    "auth.logged-out": createSimpleFormatter("my-account/logged-out"),
    "auth.callback": createSimpleFormatter("my-account/callback"),
    "auth.activate": createSimpleFormatter("my-account/activate"),
    "auth.change-password": createSimpleFormatter("my-account/settings/change-password"),
    "auth.reset-password": createSimpleFormatter("my-account/reset-password"),
    "auth.set-password": createSimpleFormatter("my-account/set-password")
  };
};
const createCheckoutFormatters = ({ path, children }) => {
  const entries = Object.entries(children).map(([route, subPath]) => [
    `checkout.${route}`,
    createSimpleFormatter(`${path}/${subPath}`)
  ]);
  return Object.fromEntries(entries);
};
function addQueryParams(url, params) {
  const q = params?.toString() ? `?${params.toString()}` : "";
  return url + q;
}
const createMyAccountFormatters = () => {
  return {
    "me.profile": createSimpleFormatter("my-account/profile"),
    "me.appointments": createSimpleFormatter("my-account/appointments"),
    "me.appointments.detail": ({ params, query }) => addQueryParams(`/my-account/appointments/${params.appointmentId}`, query),
    "me.orders": createSimpleFormatter("my-account/orders"),
    "me.orders.detail": ({ params, query }) => addQueryParams(`/my-account/orders/${params.orderId}`, query),
    "me.wishlist": createSimpleFormatter("my-account/wishlist"),
    "me.cccSubscriptions": createSimpleFormatter("my-account/ccc"),
    "me.cccSubscriptions.detail": ({ params, query }) => addQueryParams(`/my-account/ccc/${params.cccSubscriptionId}`, query),
    "me.subscriptions": createSimpleFormatter("my-account/subscriptions"),
    "me.subscriptions.detail": ({ params, query }) => addQueryParams(`/my-account/subscriptions/${params.subscriptionId}`, query),
    "me.prescription": createSimpleFormatter("my-account/prescriptions"),
    "me.settings": createSimpleFormatter("my-account/settings"),
    "me.settings.comms": createSimpleFormatter("my-account/settings/communication-preferences")
  };
};
const productDetailFormatters = (productDetailRoutes, options = { excludeSku: false }) => {
  const createFormatter = (productType) => {
    const { excludeSku } = options;
    return ({ route, params, locale, query }) => {
      if (route !== "products.detail") {
        return null;
      }
      const { slug, sku, key } = params;
      const productTypeKey = productType ?? key;
      const translatedProductType = locale && productDetailRoutes[locale] ? productDetailRoutes[locale][productTypeKey] : productDetailRoutes[productTypeKey];
      if (translatedProductType === void 0) {
        return null;
      }
      const routeParts = [""];
      if (locale) {
        routeParts.push(locale.replace("-", "_"));
      }
      routeParts.push(translatedProductType, slug);
      if (!excludeSku) {
        routeParts.push(sku);
      }
      let joinedRoute = routeParts.join("/");
      if (query) {
        joinedRoute += `?${query.toString()}`;
      }
      return joinedRoute;
    };
  };
  return {
    "products.detail": createFormatter(),
    "products.detail.accessories": createFormatter(ProductType.Accessories),
    "products.detail.contact-lenses": createFormatter(ProductType.ContactLenses),
    "products.detail.frames": createFormatter(ProductType.Frames),
    "products.detail.ready-readers": createFormatter(ProductType.ReadyReaders),
    "products.detail.solutions": createFormatter(ProductType.Solutions),
    "products.detail.sunglasses": createFormatter(ProductType.Sunglasses)
  };
};
const productListFormatter = (productRoutes) => {
  const formatter = ({
    params,
    locale,
    query: defaultQuery
  }) => {
    const { type, facetQueryParams, subCategory } = params;
    const localisedProductType = locale && productRoutes[locale] ? productRoutes[locale][type] : productRoutes[type];
    let query = new URLSearchParams();
    if (defaultQuery) {
      query = defaultQuery;
    }
    if (facetQueryParams) {
      Object.keys(facetQueryParams).forEach((param) => {
        if (facetQueryParams[param] && !query.has(param)) {
          query.set(param, facetQueryParams[param]?.toString() ?? "");
        }
      });
    }
    const routeParts = [""];
    if (locale) {
      routeParts.push(locale.replace("-", "_"));
    }
    routeParts.push(localisedProductType);
    if (subCategory) {
      routeParts.push(subCategory);
    }
    const qs = query.toString();
    return `${routeParts.join("/")}${qs ? `?${qs}` : ""}`;
  };
  return {
    "products.list": formatter
  };
};
const appointmentFormatter = (path) => (config) => {
  const withoutSlash = (string) => string.startsWith("/") ? string.slice(1) : string;
  const url = [withoutSlash(path)];
  const {
    publicRuntimeConfig: { featureConfigs }
  } = getConfig();
  const globalStoreId = config.params?.storeId;
  let query = new URLSearchParams();
  if (config.query) {
    query = config.query;
  }
  if (config.params) {
    Object.keys(config.params).forEach((param) => {
      config?.params?.[param] && query.set(param, config.params[param]);
    });
  }
  if (featureConfigs?.appointmentBookingAlternativeUrl?.length && featureConfigs?.appointmentBookingAlternativeUrlStores?.includes(globalStoreId)) {
    url[0] = withoutSlash(featureConfigs?.appointmentBookingAlternativeUrl);
  }
  if (globalStoreId && config.params?.storeId && featureConfigs?.appointmentBookingUrlPaddedLength) {
    query.set(
      "storeId",
      globalStoreId.padStart(featureConfigs?.appointmentBookingUrlPaddedLength, "0")
    );
  }
  if (config.locale) {
    url.unshift(config.locale);
  }
  const qs = query.toString();
  return `/${url.join("/")}${qs ? `?${qs}` : ""}`;
};
const createMultilangFormatter = (path) => (config) => {
  if (!config?.locale) return "/";
  const route = createSimpleFormatter(path[config.locale])(config);
  const locale = config.locale.replace("-", "_");
  return `/${locale}${route}`;
};
export {
  appointmentFormatter,
  createAuthFormatters,
  createCheckoutFormatters,
  createMultilangFormatter,
  createMyAccountFormatters,
  productDetailFormatters,
  productListFormatter
};
//# sourceMappingURL=formatters.js.map
